import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

interface FacilityOption {
  id: string;
  name: string;
  patientId: string;
  accent?: 'red' | 'blue';
}

interface FacilitatorOption {
  id: string;
  name: string;
  email: string;
  facilityId: string;
}

const DEFAULT_ORIGIN_FACILITY: FacilityOption = {
  id: 'jdh-dc-001',
  name: 'John Dell Hospital',
  patientId: 'jdh-dc-001',
  accent: 'red'
};

const DEFAULT_FACILITIES: FacilityOption[] = [
  { id: 'jdh-dc-001', name: 'John Dell Hospital', patientId: 'jdh-dc-001', accent: 'red' },
  { id: 'ehh-dc-001', name: 'Evelyn Hopkins Hospital', patientId: 'ehh-dc-001', accent: 'blue' },
  { id: 'zsh-dc-001', name: 'Zelenia Stanton Hospital', patientId: 'zsh-dc-001' },
  { id: 'ddh-dc-001', name: 'Dahlia Durham Hospital', patientId: 'ddh-dc-001' }
];

const DEFAULT_FACILITATORS: FacilitatorOption[] = [
  { id: 'fac-1', name: 'John Carter', email: 'john.carter@johndellhospital.com', facilityId: 'jdh-dc-001' },
  { id: 'fac-2', name: 'Fiona Lewis', email: 'fiona.lewis@evelynhopkins.com', facilityId: 'ehh-dc-001' },
  { id: 'fac-3', name: 'Daniel Paul', email: 'daniel.paul@evelynhopkins.com', facilityId: 'ehh-dc-001' },
  { id: 'fac-4', name: 'Sara Le Roux', email: 'sara.leroux@evelynhopkins.com', facilityId: 'ehh-dc-001' },
  { id: 'fac-5', name: 'John Cougar', email: 'john.cougar@evelynhopkins.com', facilityId: 'ehh-dc-001' },
  { id: 'fac-6', name: 'Maya Everett', email: 'maya.everett@zeleniastanton.com', facilityId: 'zsh-dc-001' },
  { id: 'fac-7', name: 'Luis Navarro', email: 'luis.navarro@zeleniastanton.com', facilityId: 'zsh-dc-001' },
  { id: 'fac-8', name: 'Jade Patel', email: 'jade.patel@dahliadurham.org', facilityId: 'ddh-dc-001' },
  { id: 'fac-9', name: 'Ethan Miles', email: 'ethan.miles@dahliadurham.org', facilityId: 'ddh-dc-001' }
];

@Component({
    selector: 'app-facility-user-selection-mock',
    templateUrl: './facility-user-selection-mock.component.html',
    styleUrls: ['./facility-user-selection-mock.component.scss'],
    standalone: false
})
export class FacilityUserSelectionMockComponent {
  currentStep = 1;

  @Input() originFacility: FacilityOption = DEFAULT_ORIGIN_FACILITY;
  @Input() facilities: FacilityOption[] = DEFAULT_FACILITIES;
  @Input() facilitators: FacilitatorOption[] = DEFAULT_FACILITATORS;

  @Output() selectionConfirmed = new EventEmitter<{
    facility: FacilityOption | undefined;
    facilitator: FacilitatorOption | undefined;
  }>();
  @Output() modifyRequested = new EventEmitter<void>();
  @Output() flowReset = new EventEmitter<void>();

  selectedFacility?: FacilityOption;
  selectedFacilitator?: FacilitatorOption;
  statusMessage = '';

  constructor(private dialogRef: MatDialogRef<FacilityUserSelectionMockComponent>) {}

  selectFacility(option: FacilityOption): void {
    this.selectedFacility = option;
    if (this.currentStep > 1) {
      this.selectedFacilitator = undefined;
    }
  }

  formatFacilityName(name: string): string {
    return name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-');
  }

  selectFacilitator(option: FacilitatorOption): void {
    this.selectedFacilitator = option;
  }

  get facilitatorOptions(): FacilitatorOption[] {
    return this.selectedFacility
      ? this.facilitators.filter(f => f.facilityId === this.selectedFacility?.id)
      : [];
  }

  nextStep(): void {
    if (this.currentStep === 1 && this.selectedFacility) {
      this.currentStep = 2;
      this.statusMessage = '';
      return;
    }

    if (this.currentStep === 2 && this.selectedFacilitator) {
      this.currentStep = 3;
      this.statusMessage = '';
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep = this.currentStep - 1;
    }
  }

  resetFlow(): void {
    this.currentStep = 1;
    this.selectedFacility = undefined;
    this.selectedFacilitator = undefined;
    this.statusMessage = '';
  }

  keepSelection(): void {
    this.statusMessage = 'Selections saved (mocked action).';
    this.selectionConfirmed.emit({
      facility: this.selectedFacility,
      facilitator: this.selectedFacilitator
    });
    this.dialogRef.close();
  }

  modifySelection(): void {
    this.statusMessage = '';
    this.currentStep = 1;
    this.modifyRequested.emit();
  }

  onReset(): void {
    this.resetFlow();
    this.flowReset.emit();
  }
}

